import {
  entries,
  filter,
  flatMap,
  forEach,
  fromEntries,
  isArray,
  isBoolean,
  isDeepEqual,
  isNumber,
  isPlainObject,
  isPromise,
  isString,
  map,
  pipe,
  splice,
  unique
} from 'remeda'

// Private types ---------------------------------------------------------------

type JsonPrimitive = boolean | number | string | null

type Ownership = 'server' | 'interactive' | 'draft'

type PreserveContract = Readonly<{
  attributes: readonly string[]
  styles: readonly string[]
}>

type DraftState = Readonly<{
  generation: number
  pendingRef?: number
  serverValues: LivePropValues
  values: LivePropValues
}>

type Reconciliation = Readonly<{
  draftMatchesServer: boolean
  values: LivePropValues
}>

type PatchJob = Readonly<{
  element: HTMLElement
  revision: number
  values: LivePropValues
}>

type PatchBatch = Readonly<{
  container: HTMLElement
  jobs: Map<HTMLElement, PatchJob>
}>

type PushDetail = Readonly<{
  loadingComplete: PromiseLike<unknown>
  ref: number
}>

type SyncState = {
  readonly adapters: WeakMap<HTMLElement, LivePropsAdapter>
  readonly applyControllers: WeakMap<HTMLElement, AbortController>
  batches: readonly PatchBatch[]
  readonly draftStates: WeakMap<HTMLElement, DraftState>
  readonly latestPatchRevisions: WeakMap<HTMLElement, number>
  nextPatchRevision: number
}

// Public types ----------------------------------------------------------------

export interface JsonObject {
  readonly [key: string]: JsonValue
}

export type JsonValue = JsonPrimitive | readonly JsonValue[] | JsonObject

export type LivePropValues = Readonly<Record<string, JsonValue>>

export type LivePropsContract = Readonly<{
  server: LivePropValues
  interactive: LivePropValues
  draft: LivePropValues
  preserve: PreserveContract
}>

export type LivePropsAdapter = Readonly<{
  read: (propertyNames: readonly string[]) => LivePropValues
  apply: (values: LivePropValues, signal: AbortSignal) => Promise<void> | void
}>

export type LivePropsSync = Readonly<{
  bind: (element: HTMLElement, adapter: LivePropsAdapter) => () => void
  connect: (root?: Document) => () => void
  onBeforeElUpdated: (from: Element, to: Element) => void
  onNodeAdded: (node: Node) => void
  onPatchEnd: (container: HTMLElement) => void
  onPatchStart: (container: HTMLElement) => void
}>

// Constants -------------------------------------------------------------------

const LIVE_PROPS_ATTRIBUTE = 'data-live-props'
const LIVE_PROPS_ADAPTER_ATTRIBUTE = 'data-live-props-adapter'
const OWNERSHIP_MODES = [
  'server',
  'interactive',
  'draft'
] as const satisfies readonly Ownership[]
const CONTRACT_KEYS = [...OWNERSHIP_MODES, 'preserve'] as const
const EMPTY_PRESERVE: PreserveContract = { attributes: [], styles: [] }
const FORBIDDEN_PROPERTIES = new Set(['__proto__', 'constructor', 'prototype'])
const FORBIDDEN_ATTRIBUTES = new Set(['id', LIVE_PROPS_ATTRIBUTE])
const RESERVED_ATTRIBUTE_PREFIXES = ['phx-', 'data-phx-', 'data-live-']
const RESERVED_ATTRIBUTE_ROOTS = [
  ...FORBIDDEN_ATTRIBUTES,
  ...RESERVED_ATTRIBUTE_PREFIXES
]
const PROPERTY_NAME_PATTERN = /^[A-Za-z_$][A-Za-z0-9_$]*$/
const PRESERVED_ATTRIBUTE_PATTERN = /^[a-z][a-z0-9:_-]*\*?$/
const PRESERVED_STYLE_PATTERN = /^(?:--)?[a-z][a-z0-9-]*$/

// Private functions -----------------------------------------------------------

// Reports recoverable metadata and adapter problems during development.
const warn = (message: string, element?: Element): void => {
  if (process.env.NODE_ENV !== 'production') {
    console.warn(`[live-props] ${message}`, element)
  }
}

// Accepts numbers that JSON can represent without changing their value.
const isFiniteNumber = (value: unknown): value is number =>
  isNumber(value) && Number.isFinite(value)

// Recognizes the scalar values allowed inside live props.
const isJsonPrimitive = (value: unknown): value is JsonPrimitive =>
  value === null || isBoolean(value) || isFiniteNumber(value) || isString(value)

// Recursively checks that a value can safely cross the JSON boundary.
const isJsonValue = (value: unknown): value is JsonValue =>
  isJsonPrimitive(value) ||
  (isArray(value) && value.every(isJsonValue)) ||
  (isPlainObject(value) && Object.values(value).every(isJsonValue))

// Accepts safe JavaScript property names only.
const isPropertyName = (value: string): boolean =>
  PROPERTY_NAME_PATTERN.test(value) && !FORBIDDEN_PROPERTIES.has(value)

// Checks own properties without invoking inherited accessors.
const ownsProperty = (value: object, property: PropertyKey): boolean =>
  Object.getOwnPropertyDescriptor(value, property) !== undefined

// Checks that an object contains only the named keys.
const hasOnlyKeys = (
  value: Readonly<Record<PropertyKey, unknown>>,
  allowedKeys: readonly string[]
): boolean => Object.keys(value).every((key) => allowedKeys.includes(key))

// Checks one property/value pair from an ownership group.
const isLivePropEntry = (
  entry: readonly [string, unknown]
): entry is readonly [string, JsonValue] =>
  isPropertyName(entry[0]) && isJsonValue(entry[1])

// Parses one ownership group into trusted live prop values.
const parseValues = (value: unknown): LivePropValues | undefined => {
  if (!isPlainObject(value)) {
    return undefined
  }

  const valueEntries = entries(value)

  return valueEntries.every(isLivePropEntry)
    ? (fromEntries(valueEntries) as LivePropValues)
    : undefined
}

// Checks whether an exact attribute belongs to LiveView.
const isReservedAttribute = (value: string): boolean =>
  FORBIDDEN_ATTRIBUTES.has(value) ||
  RESERVED_ATTRIBUTE_PREFIXES.some((prefix) => value.startsWith(prefix))

// Prevents a wildcard from covering any LiveView-owned attribute family.
const wildcardCoversReservedAttribute = (value: string): boolean => {
  if (!value.endsWith('*')) {
    return false
  }

  const prefix = value.slice(0, -1)

  return RESERVED_ATTRIBUTE_ROOTS.some((reserved) =>
    reserved.startsWith(prefix)
  )
}

// Accepts one safe exact or prefix-wildcard attribute selector.
const isPreservedAttribute = (value: unknown): value is string =>
  isString(value) &&
  PRESERVED_ATTRIBUTE_PATTERN.test(value) &&
  !isReservedAttribute(value) &&
  !wildcardCoversReservedAttribute(value)

// Accepts one CSS property or custom property name.
const isPreservedStyle = (value: unknown): value is string =>
  isString(value) && PRESERVED_STYLE_PATTERN.test(value)

// Checks an array with a string-narrowing predicate.
const isStringArray = (
  value: unknown,
  predicate: (item: unknown) => item is string
): value is readonly string[] => isArray(value) && value.every(predicate)

// Parses the optional runtime state preservation rules.
const parsePreserve = (value: unknown): PreserveContract | undefined => {
  if (value === undefined) {
    return EMPTY_PRESERVE
  }

  if (!(isPlainObject(value) && hasOnlyKeys(value, ['attributes', 'styles']))) {
    return undefined
  }

  const attributes = value.attributes ?? []
  const styles = value.styles ?? []

  return isStringArray(attributes, isPreservedAttribute) &&
    isStringArray(styles, isPreservedStyle)
    ? { attributes, styles }
    : undefined
}

// Checks that each property has exactly one owner.
const propertyOwnersAreDisjoint = (
  modes: Readonly<Record<Ownership, LivePropValues>>
): boolean => {
  const properties = pipe(
    OWNERSHIP_MODES,
    flatMap((mode) => Object.keys(modes[mode]))
  )

  return unique(properties).length === properties.length
}

// Decodes JSON without exposing parser exceptions to LiveView.
const decodeJson = (encoded: string): unknown | undefined => {
  try {
    return JSON.parse(encoded) as unknown
  } catch {
    return undefined
  }
}

// Builds a trusted contract from decoded JSON data.
const parseContract = (decoded: unknown): LivePropsContract | undefined => {
  if (!(isPlainObject(decoded) && hasOnlyKeys(decoded, CONTRACT_KEYS))) {
    return undefined
  }

  const server = parseValues(decoded.server ?? {})
  const interactive = parseValues(decoded.interactive ?? {})
  const draft = parseValues(decoded.draft ?? {})
  const preserve = parsePreserve(decoded.preserve)

  if (!(server && interactive && draft && preserve)) {
    return undefined
  }

  const modes = { server, interactive, draft }

  return propertyOwnersAreDisjoint(modes) ? { ...modes, preserve } : undefined
}

// Parses the attribute string used by both the public API and DOM adapter.
const parseEncodedContract = (
  encoded: string | null
): LivePropsContract | undefined =>
  encoded === null ? undefined : parseContract(decodeJson(encoded))

// Maps properties while letting each ownership mode choose its value.
const entriesForProperties = (
  properties: LivePropValues,
  selectValue: (property: string, serverValue: JsonValue) => JsonValue
): readonly (readonly [string, JsonValue])[] =>
  pipe(
    entries(properties),
    map(
      ([property, serverValue]) =>
        [property, selectValue(property, serverValue)] as const
    )
  )

// Uses a runtime value when present, otherwise the server fallback.
const runtimeOrServer = (
  runtime: LivePropValues,
  property: string,
  serverValue: JsonValue
): JsonValue =>
  ownsProperty(runtime, property) ? (runtime[property] ?? null) : serverValue

// Chooses an interactive value from server intent and current browser state.
const interactiveValue = (
  previous: LivePropsContract | undefined,
  runtime: LivePropValues,
  property: string,
  serverValue: JsonValue
): JsonValue => {
  const serverChanged =
    previous === undefined ||
    !ownsProperty(previous.interactive, property) ||
    !isDeepEqual(previous.interactive[property], serverValue)

  return serverChanged
    ? serverValue
    : runtimeOrServer(runtime, property, serverValue)
}

// Chooses a draft value until the complete draft matches server state.
const draftValue = (
  draft: LivePropValues | undefined,
  draftMatchesServer: boolean,
  property: string,
  serverValue: JsonValue
): JsonValue =>
  draft !== undefined && !draftMatchesServer
    ? runtimeOrServer(draft, property, serverValue)
    : serverValue

// Reconciles all ownership modes as one pure state transition.
const reconcile = (
  previous: LivePropsContract | undefined,
  incoming: LivePropsContract,
  runtime: LivePropValues,
  draft: LivePropValues | undefined
): Reconciliation => {
  const draftMatchesServer =
    draft !== undefined && isDeepEqual(draft, incoming.draft)
  const interactiveEntries = entriesForProperties(
    incoming.interactive,
    (property, serverValue) =>
      interactiveValue(previous, runtime, property, serverValue)
  )
  const draftEntries = entriesForProperties(
    incoming.draft,
    (property, serverValue) =>
      draftValue(draft, draftMatchesServer, property, serverValue)
  )

  return {
    draftMatchesServer,
    values: fromEntries([
      ...entries(incoming.server),
      ...interactiveEntries,
      ...draftEntries
    ]) as LivePropValues
  }
}

// Lists every property named by a contract.
const allManagedProperties = (contract: LivePropsContract): readonly string[] =>
  pipe(
    OWNERSHIP_MODES,
    flatMap((mode) => Object.keys(contract[mode]))
  )

// Flattens the rendered contract into its initial property values.
const initialValues = (contract: LivePropsContract): LivePropValues =>
  fromEntries(
    pipe(
      OWNERSHIP_MODES,
      flatMap((mode) => entries(contract[mode]))
    )
  ) as LivePropValues

// Expands one exact or wildcard attribute preservation rule.
const matchingAttributeNames = (
  from: HTMLElement,
  to: HTMLElement,
  pattern: string
): readonly string[] => {
  if (!pattern.endsWith('*')) {
    return [pattern]
  }

  const prefix = pattern.slice(0, -1)

  return pipe(
    [...from.attributes, ...to.attributes],
    map((attribute) => attribute.name),
    filter((name) => name.startsWith(prefix)),
    unique()
  )
}

// Copies one runtime attribute, including an intentional removal.
const copyAttribute = (
  from: HTMLElement,
  to: HTMLElement,
  attribute: string
): void => {
  const value = from.getAttribute(attribute)

  if (value === null) {
    to.removeAttribute(attribute)
  } else {
    to.setAttribute(attribute, value)
  }
}

// Copies one runtime style, including priority and intentional removal.
const copyStyle = (
  from: HTMLElement,
  to: HTMLElement,
  property: string
): void => {
  const value = from.style.getPropertyValue(property)
  const priority = from.style.getPropertyPriority(property)

  if (value === '') {
    to.style.removeProperty(property)
  } else {
    to.style.setProperty(property, value, priority)
  }
}

// Carries declared runtime attributes and styles into incoming markup.
const preserveRuntimeState = (
  from: HTMLElement,
  to: HTMLElement,
  preserve: PreserveContract
): void => {
  pipe(
    preserve.attributes,
    flatMap((pattern) => matchingAttributeNames(from, to, pattern)),
    forEach((attribute) => copyAttribute(from, to, attribute))
  )
  forEach(preserve.styles, (property) => copyStyle(from, to, property))
}

// Reads one element contract and warns when its metadata is invalid.
const parseElementContract = (
  element: HTMLElement
): LivePropsContract | undefined => {
  const encoded = element.getAttribute(LIVE_PROPS_ATTRIBUTE)
  const contract = parseEncodedContract(encoded)

  if (encoded !== null && contract === undefined) {
    warn('Ignoring an invalid data-live-props contract', element)
  }

  return contract
}

// Reads JSON-safe properties directly from a standard element.
const defaultRead = (
  element: HTMLElement,
  propertyNames: readonly string[]
): LivePropValues =>
  fromEntries(
    pipe(
      propertyNames,
      flatMap((property) => {
        const value: unknown = Reflect.get(element, property)

        return property in element && isJsonValue(value)
          ? ([[property, value]] as const)
          : []
      })
    )
  ) as LivePropValues

// Applies standard properties and reports missing component APIs.
const defaultApply = (element: HTMLElement, values: LivePropValues): void => {
  forEach(entries(values), ([property, value]) => {
    if (property in element) {
      Reflect.set(element, property, value)
    } else {
      warn(
        `Property ${property} does not exist on ${element.localName}`,
        element
      )
    }
  })
}

// Recognizes a custom element by its required hyphenated name.
const isCustomElement = (element: HTMLElement): boolean =>
  element.localName.includes('-')

// Waits only when a custom element definition is still pending.
const definitionPromise = (
  element: HTMLElement
): Promise<CustomElementConstructor> | undefined =>
  isCustomElement(element) &&
  customElements.get(element.localName) === undefined
    ? customElements.whenDefined(element.localName)
    : undefined

// Finds the managed host that owns a composed browser event.
const managedRootFromEvent = (event: Event): HTMLElement | undefined => {
  const pathRoot = event
    .composedPath()
    .find(
      (item): item is HTMLElement =>
        item instanceof HTMLElement && item.hasAttribute(LIVE_PROPS_ATTRIBUTE)
    )
  const targetRoot =
    event.target instanceof HTMLElement
      ? (event.target.closest<HTMLElement>(`[${LIVE_PROPS_ATTRIBUTE}]`) ??
        undefined)
      : undefined

  return pathRoot ?? targetRoot
}

// Accepts LiveView's non-negative integer push references.
const isPushRef = (value: unknown): value is number =>
  isNumber(value) && Number.isSafeInteger(value) && value >= 0

// Extracts the acknowledgement promise from a LiveView change push.
const parsePushDetail = (event: Event): PushDetail | undefined => {
  if (!(event instanceof CustomEvent && isPlainObject(event.detail))) {
    return undefined
  }

  const { eventType, loadingComplete, ref } = event.detail

  return eventType === 'change' && isPushRef(ref) && isPromise(loadingComplete)
    ? { loadingComplete, ref }
    : undefined
}

// Lists a managed root and all managed descendants.
const managedElements = (root: ParentNode): readonly HTMLElement[] => {
  const descendants = Array.from(
    root.querySelectorAll<HTMLElement>(`[${LIVE_PROPS_ATTRIBUTE}]`)
  )

  return root instanceof HTMLElement && root.hasAttribute(LIVE_PROPS_ATTRIBUTE)
    ? [root, ...descendants]
    : descendants
}

// Creates the isolated mutable state used at the DOM boundary.
const createSyncState = (): SyncState => ({
  adapters: new WeakMap(),
  applyControllers: new WeakMap(),
  batches: [],
  draftStates: new WeakMap(),
  latestPatchRevisions: new WeakMap(),
  nextPatchRevision: 0
})

// Reads through a composite adapter or the default property reader.
const readManagedValues = (
  state: SyncState,
  element: HTMLElement,
  propertyNames: readonly string[]
): LivePropValues => {
  const values =
    state.adapters.get(element)?.read(propertyNames) ??
    defaultRead(element, propertyNames)
  const validEntries = pipe(
    entries(values),
    filter(
      ([property, value]) =>
        propertyNames.includes(property) && isJsonValue(value)
    )
  )

  if (validEntries.length !== Object.keys(values).length) {
    warn('Adapter returned unmanaged or non-JSON property values', element)
  }

  return fromEntries(validEntries) as LivePropValues
}

// Checks that async work still belongs to the latest patch.
const isCurrentRevision = (
  state: SyncState,
  element: HTMLElement,
  revision: number | undefined
): boolean =>
  revision === undefined || state.latestPatchRevisions.get(element) === revision

// Checks that an async property application is still safe to run.
const canApply = (
  state: SyncState,
  element: HTMLElement,
  controller: AbortController,
  revision: number | undefined
): boolean =>
  !controller.signal.aborted &&
  element.isConnected &&
  isCurrentRevision(state, element, revision)

// Runs one property group after upgrade and skips stale async work.
const runApply = async (
  state: SyncState,
  element: HTMLElement,
  values: LivePropValues,
  controller: AbortController,
  revision: number | undefined
): Promise<void> => {
  const pendingDefinition = state.adapters.has(element)
    ? undefined
    : definitionPromise(element)

  if (pendingDefinition) {
    await pendingDefinition
  }

  if (!canApply(state, element, controller, revision)) {
    return
  }

  const adapter = state.adapters.get(element)
  const result = adapter
    ? adapter.apply(values, controller.signal)
    : defaultApply(element, values)

  if (isPromise(result)) {
    await result
  }
}

// Cancels the previous application and starts the newest one.
const applyValues = (
  state: SyncState,
  element: HTMLElement,
  values: LivePropValues,
  revision?: number
): void => {
  if (!isCurrentRevision(state, element, revision)) {
    return
  }

  state.applyControllers.get(element)?.abort()

  const controller = new AbortController()
  state.applyControllers.set(element, controller)
  runApply(state, element, values, controller, revision).catch(
    (error: unknown) => {
      if (!controller.signal.aborted) {
        warn(`Failed to apply properties: ${String(error)}`, element)
      }
    }
  )
}

// Applies the latest server-rendered contract to one managed element.
const applyRenderedState = (state: SyncState, element: HTMLElement): void => {
  const contract = parseElementContract(element)

  if (!contract) {
    return
  }

  const applyInitialValues = (): void =>
    applyValues(state, element, initialValues(contract))

  if (state.adapters.has(element) || isCustomElement(element)) {
    applyInitialValues()
  } else if (element.hasAttribute(LIVE_PROPS_ADAPTER_ATTRIBUTE)) {
    return
  } else {
    queueMicrotask(() => {
      if (element.isConnected) {
        applyInitialValues()
      }
    })
  }
}

// Records the newest local draft after an input or change event.
const markDraft = (state: SyncState, event: Event): void => {
  const element = managedRootFromEvent(event)
  const contract = element ? parseElementContract(element) : undefined

  if (!(element && contract && Object.keys(contract.draft).length > 0)) {
    return
  }

  const values = readManagedValues(state, element, Object.keys(contract.draft))

  if (isDeepEqual(values, contract.draft)) {
    state.draftStates.delete(element)
    return
  }

  const generation = (state.draftStates.get(element)?.generation ?? 0) + 1
  state.draftStates.set(element, {
    generation,
    serverValues: contract.draft,
    values
  })
}

// Checks that an acknowledgement still belongs to the newest local edit.
const isLatestAcknowledgement = (
  latest: DraftState | undefined,
  pending: DraftState
): boolean =>
  latest?.generation === pending.generation &&
  latest.pendingRef === pending.pendingRef

// Removes push identity while retaining a draft the server did not accept.
const withoutAcknowledgement = ({
  generation,
  serverValues,
  values
}: DraftState): DraftState => ({ generation, serverValues, values })

// Checks whether the server changed the rendered draft during this push.
const serverDraftChanged = (
  pending: DraftState,
  contract: LivePropsContract
): boolean => !isDeepEqual(pending.serverValues, contract.draft)

// Finishes one acknowledgement only when no newer edit replaced it.
const finishAcknowledgement = (
  state: SyncState,
  element: HTMLElement,
  pending: DraftState
): void => {
  const latest = state.draftStates.get(element)
  const contract = parseElementContract(element)

  if (!(isLatestAcknowledgement(latest, pending) && contract)) {
    return
  }

  if (!serverDraftChanged(pending, contract)) {
    state.draftStates.set(element, withoutAcknowledgement(pending))
    return
  }

  state.draftStates.delete(element)
  applyValues(state, element, contract.draft)
}

// Links the current draft to its LiveView push acknowledgement.
const acknowledgeDraft = (state: SyncState, event: Event): void => {
  const element = managedRootFromEvent(event)
  const detail = parsePushDetail(event)
  const current = element ? state.draftStates.get(element) : undefined

  if (!(element && detail && current)) {
    return
  }

  const pending = { ...current, pendingRef: detail.ref }
  state.draftStates.set(element, pending)
  detail.loadingComplete.then(
    () => finishAcknowledgement(state, element, pending),
    () => undefined
  )
}

// Checks whether a managed root belongs to the reset form.
const isAssociatedWithForm = (
  element: HTMLElement,
  form: HTMLFormElement
): boolean => {
  if (form.contains(element)) {
    return true
  }

  if (form.id === '') {
    return false
  }

  return (
    element.getAttribute('form') === form.id ||
    Array.from(element.querySelectorAll('[form]')).some(
      (control) => control.getAttribute('form') === form.id
    )
  )
}

// Clears and reapplies one managed draft after a form reset.
const resetDraft = (state: SyncState, element: HTMLElement): void => {
  const contract = parseElementContract(element)

  if (contract && Object.keys(contract.draft).length > 0) {
    state.draftStates.delete(element)
    applyValues(state, element, contract.draft)
  }
}

// Restores every managed draft associated with the reset form.
const resetDrafts = (state: SyncState, root: Document, event: Event): void => {
  if (!(event.target instanceof HTMLFormElement)) {
    return
  }

  const form = event.target

  queueMicrotask(() => {
    pipe(
      managedElements(root),
      filter((element) => isAssociatedWithForm(element, form)),
      forEach((element) => resetDraft(state, element))
    )
  })
}

// Registers one document listener and returns its cleanup.
const listen = (
  root: Document,
  type: string,
  listener: EventListener
): (() => void) => {
  root.addEventListener(type, listener, true)

  return () => root.removeEventListener(type, listener, true)
}

// Starts document-level draft tracking and hydrates existing elements.
const connect = (state: SyncState, root: Document): (() => void) => {
  const cleanups = [
    listen(root, 'input', (event) => markDraft(state, event)),
    listen(root, 'change', (event) => markDraft(state, event)),
    listen(root, 'phx:push', (event) => acknowledgeDraft(state, event)),
    listen(root, 'reset', (event) => resetDrafts(state, root, event))
  ]

  forEach(managedElements(root), (element) =>
    applyRenderedState(state, element)
  )

  return () => forEach(cleanups, (cleanup) => cleanup())
}

// Removes adapter-owned state when its hook is destroyed.
const unbind = (
  state: SyncState,
  element: HTMLElement,
  adapter: LivePropsAdapter
): void => {
  if (state.adapters.get(element) !== adapter) {
    return
  }

  state.applyControllers.get(element)?.abort()
  state.adapters.delete(element)
  state.applyControllers.delete(element)
  state.draftStates.delete(element)
}

// Registers a composite property adapter and hydrates it immediately.
const bind = (
  state: SyncState,
  element: HTMLElement,
  adapter: LivePropsAdapter
): (() => void) => {
  state.adapters.set(element, adapter)
  applyRenderedState(state, element)

  return () => unbind(state, element, adapter)
}

// Returns matching HTML elements for one LiveView element update.
const matchingHtmlElements = (
  from: Element,
  to: Element
): readonly [HTMLElement, HTMLElement] | undefined =>
  from instanceof HTMLElement &&
  to instanceof HTMLElement &&
  from.tagName === to.tagName
    ? [from, to]
    : undefined

// Advances the revision that invalidates stale queued work.
const revise = (state: SyncState, element: HTMLElement): number => {
  state.nextPatchRevision += 1
  state.latestPatchRevisions.set(element, state.nextPatchRevision)

  return state.nextPatchRevision
}

// Clears local state when the server removes the live props contract.
const removeContractState = (state: SyncState, element: HTMLElement): void => {
  if (!element.hasAttribute(LIVE_PROPS_ATTRIBUTE)) {
    return
  }

  state.draftStates.delete(element)
  state.applyControllers.get(element)?.abort()
  revise(state, element)
}

// Queues reconciled values in the active patch or the next microtask.
const queueReconciliation = (
  state: SyncState,
  element: HTMLElement,
  values: LivePropValues
): void => {
  const revision = revise(state, element)
  const batch = state.batches[state.batches.length - 1]

  if (batch) {
    batch.jobs.set(element, { element, revision, values })
  } else {
    warn('onBeforeElUpdated ran outside a LiveView patch batch', element)
    queueMicrotask(() => applyValues(state, element, values, revision))
  }
}

// Reconciles one managed element before LiveView morphs its attributes.
const reconcileElement = (
  state: SyncState,
  from: HTMLElement,
  to: HTMLElement,
  incoming: LivePropsContract
): void => {
  preserveRuntimeState(from, to, incoming.preserve)

  if (
    to.hasAttribute(LIVE_PROPS_ADAPTER_ATTRIBUTE) &&
    !state.adapters.has(from)
  ) {
    state.draftStates.delete(from)
    state.applyControllers.get(from)?.abort()
    revise(state, from)
    return
  }

  const result = reconcile(
    parseElementContract(from),
    incoming,
    readManagedValues(state, from, allManagedProperties(incoming)),
    state.draftStates.get(from)?.values
  )

  if (result.draftMatchesServer || Object.keys(incoming.draft).length === 0) {
    state.draftStates.delete(from)
  }

  queueReconciliation(state, from, result.values)
}

// Handles one LiveView pre-morph callback without tag-specific branches.
const beforeElementUpdated = (
  state: SyncState,
  from: Element,
  to: Element
): void => {
  const elements = matchingHtmlElements(from, to)

  if (!elements) {
    return
  }

  const [current, incomingElement] = elements
  const incoming = parseElementContract(incomingElement)

  if (incoming) {
    reconcileElement(state, current, incomingElement, incoming)
  } else {
    removeContractState(state, current)
  }
}

// Phoenix reports every inserted node separately; hydrate only that node.
const nodeAdded = (state: SyncState, node: Node): void => {
  if (node instanceof HTMLElement) {
    applyRenderedState(state, node)
  }
}

// Opens a batch for one LiveView patch transaction.
const patchStarted = (state: SyncState, container: HTMLElement): void => {
  state.batches = [...state.batches, { container, jobs: new Map() }]
}

// Removes and returns the newest batch for a container.
const takePatchBatch = (
  state: SyncState,
  container: HTMLElement
): PatchBatch | undefined => {
  const index = state.batches
    .map((batch) => batch.container)
    .lastIndexOf(container)

  if (index === -1) {
    return undefined
  }

  const batch = state.batches[index]
  state.batches = splice(state.batches, index, 1)

  return batch
}

// Applies all property groups after one LiveView morph finishes.
const patchEnded = (state: SyncState, container: HTMLElement): void => {
  const batch = takePatchBatch(state, container)

  if (!batch) {
    warn('onPatchEnd did not match an active patch batch', container)
    return
  }

  forEach([...batch.jobs.values()], ({ element, revision, values }) =>
    applyValues(state, element, values, revision)
  )
}

// Builds the public callbacks around one isolated synchronization state.
const createSync = (): LivePropsSync => {
  const state = createSyncState()

  return {
    bind: (element, adapter) => bind(state, element, adapter),
    connect: (root = document) => connect(state, root),
    onBeforeElUpdated: (from, to) => beforeElementUpdated(state, from, to),
    onNodeAdded: (node) => nodeAdded(state, node),
    onPatchEnd: (container) => patchEnded(state, container),
    onPatchStart: (container) => patchStarted(state, container)
  }
}

// Public functions ------------------------------------------------------------

// Parses and validates one server-rendered live props contract.
export const parseLiveProps = (
  encoded: string | null
): LivePropsContract | undefined => parseEncodedContract(encoded)

// Reads one server-rendered value without exposing the full contract.
export const readRenderedLiveProp = (
  element: HTMLElement,
  ownership: Ownership,
  property: string
): JsonValue | undefined =>
  parseEncodedContract(element.getAttribute(LIVE_PROPS_ATTRIBUTE))?.[ownership][
    property
  ]

// Reconciles server intent, browser state, and an optional local draft.
export const reconcileLiveProps = (
  previous: LivePropsContract | undefined,
  incoming: LivePropsContract,
  runtime: LivePropValues,
  draft: LivePropValues | undefined
): Reconciliation => reconcile(previous, incoming, runtime, draft)

// Creates an independent LiveView synchronization engine.
export const createLivePropsSync = (): LivePropsSync => createSync()

export const livePropsSync = createLivePropsSync()

// Registers a composite control with the shared synchronization engine.
export const bindLiveProps = (
  element: HTMLElement,
  adapter: LivePropsAdapter
): (() => void) => livePropsSync.bind(element, adapter)
